import React from 'react';

import isEmpty from 'lodash/isEmpty';

import { ASK_AI } from '@lumapps/ask-ai/keys';
import { margin, useClassnames } from '@lumapps/classnames';
import { useDataAttributes } from '@lumapps/data-attributes';
import { Pagination } from '@lumapps/lumx-layouts/components/Pagination';
import { PaginationMode } from '@lumapps/lumx-layouts/types';
import {
    mdiArrowExpand,
    mdiCheckboxMarkedCircleOutline,
    mdiMinus,
    mdiSparkles,
    mdiSubdirectoryArrowLeft,
} from '@lumapps/lumx/icons';
import {
    Button,
    ColorPalette,
    Emphasis,
    FlexBox,
    HeadingLevelProvider,
    Icon,
    IconButton,
    Orientation,
    Placement,
    Popover,
    ProgressCircular,
    Size,
    Text,
    TextField,
    Typography,
} from '@lumapps/lumx/react';
import { STRUCTURED_CONTENT_TOOLBAR_CLASSNAME } from '@lumapps/structured-content/constants';
import { GLOBAL, useTranslate } from '@lumapps/translations';
import { WREX_MARKDOWN_ELEMENTS, WREX_MARKDOWN_MARKS } from '@lumapps/wrex-markdown/constants';
import { ContentView } from '@lumapps/wrex/components/ContentView';
import { fromMarkdown } from '@lumapps/wrex/serialization/markdown/fromMarkdown';
import { ReactEditor, useSlate } from '@lumapps/wrex/slate';
import { Nodes } from '@lumapps/wrex/types/core';

import { GENERATIVE_AI_TOOLBAR_CLASSNAME } from '../../constants';
import { UseRefineContentOutput } from '../../hooks/useRefineContentDialog';
import { WREX_GENERATED_CONTENT } from '../../keys';

import './index.scss';

const CLASSNAME = 'refine-content-dialog';
// Minimum height of the suggestion's content view
const SUGGESTION_MIN_HEIGHT = 150;
// Minimum height of the dialog (suggestion min height + rest of the dialog)
const DIALOG_MIN_HEIGHT = 326;

export interface RefineContentDialogProps
    extends Pick<
        UseRefineContentOutput,
        'isRefiningContent' | 'refinementHistory' | 'onCancelRefinement' | 'onInsertNewContent' | 'onRefineContent'
    > {
    anchorRef: React.RefObject<HTMLElement>;
    editorRef: React.RefObject<HTMLDivElement>;
}

export const RefineContentDialog = ({
    anchorRef,
    editorRef,
    isRefiningContent,
    refinementHistory,
    onCancelRefinement,
    onInsertNewContent,
    onRefineContent,
}: RefineContentDialogProps) => {
    const { translateKey, pluralize } = useTranslate();
    const { get } = useDataAttributes('ai-adapt-selection-ask-ai');
    const { block, element } = useClassnames(CLASSNAME);
    const editor = useSlate() as ReactEditor;

    const suggestionRef = React.useRef<HTMLDivElement>(null);
    const dialogRef = React.useRef<HTMLDivElement>(null);
    const inputRef = React.useRef<HTMLInputElement>(null);
    const boundaryRef = React.useRef<HTMLDivElement>(null);

    const [isExpanded, setIsExpanded] = React.useState(false);
    const [dialogHeightSettings, setDialogHeightSettings] = React.useState<{
        dialogHeight: number | undefined;
        doSuggestionNeedScroll: boolean;
    }>({
        dialogHeight: undefined,
        doSuggestionNeedScroll: false,
    });
    const [prompt, setPrompt] = React.useState('');
    const [historyPosition, setHistoryPosition] = React.useState(refinementHistory ? refinementHistory.length - 1 : 0);

    const currentlyDisplayedSlateContent = refinementHistory?.[historyPosition]
        ? fromMarkdown(refinementHistory[historyPosition].refinedContent)
        : // Get nodes from the selection range
          (editor.getFragment() as Nodes);

    const placeholder = React.useMemo(() => {
        // If no refinement history (meaning no previous refinements), use the initial Ask anything placeholder
        if (!refinementHistory || refinementHistory.length === 0) {
            return translateKey(ASK_AI.ASK_ANYTHING);
        }
        // If there is a refinement history, use the last user prompt as placeholder
        // Fallback to the default prompt if the only history entry is not a user prompt (e.g. button prompt)
        return refinementHistory[historyPosition].userPrompt || translateKey(WREX_GENERATED_CONTENT.OR_ASK_ANYTHING);
    }, [refinementHistory, historyPosition, translateKey]);

    // Compute the maximum height of the editor
    // based on it's content height, and the available space in the editor
    const computeDialogHeight = React.useCallback(() => {
        const dialogSettings: typeof dialogHeightSettings = {
            dialogHeight: undefined,
            doSuggestionNeedScroll: false,
        };

        if (suggestionRef.current && suggestionRef.current.firstChild) {
            const boxHeight = suggestionRef.current.scrollHeight;
            const suggestionHeight = suggestionRef.current.getBoundingClientRect().height;
            dialogSettings.doSuggestionNeedScroll =
                suggestionHeight > SUGGESTION_MIN_HEIGHT || suggestionHeight < boxHeight;
        }

        if (!editorRef || !editorRef.current) {
            setDialogHeightSettings(dialogSettings);
            return;
        }
        const toolbar = editorRef.current.querySelector(`.${STRUCTURED_CONTENT_TOOLBAR_CLASSNAME}`);
        const aiToolbar = editorRef.current.querySelector(`.${GENERATIVE_AI_TOOLBAR_CLASSNAME}`);
        if (!toolbar || !aiToolbar) {
            setDialogHeightSettings(dialogSettings);
            return;
        }
        const innerHeight = Math.abs(toolbar.getBoundingClientRect().bottom - aiToolbar.getBoundingClientRect().top);
        dialogSettings.dialogHeight = innerHeight;

        setDialogHeightSettings(dialogSettings);
    }, [editorRef]);

    // If the height will be too small, allow to overflow the toolbar
    const shouldOverflowToolbar =
        dialogHeightSettings.dialogHeight === undefined || dialogHeightSettings.dialogHeight < DIALOG_MIN_HEIGHT;
    // Get maximum height of the dialog (dialogHeight) or minimum height (DIALOG_MIN_HEIGHT) if the dialog is too small
    const dialogMaxHeight = Math.max(dialogHeightSettings.dialogHeight ?? 0, DIALOG_MIN_HEIGHT);

    const onSubmitPrompt = React.useCallback(async () => {
        const newPrompt = prompt.trim();
        setPrompt('');
        const newHistory = await onRefineContent({ refinement: newPrompt, isUserPrompt: true });
        setHistoryPosition(newHistory ? newHistory.length - 1 : 0);
        // Focus the input field
        inputRef.current?.focus({ preventScroll: true });
    }, [prompt, onRefineContent]);

    // Press Enter to submit a new prompt
    const onKeyDown = React.useCallback(
        (ev: React.KeyboardEvent<HTMLInputElement>) => {
            if (ev.key === 'Enter') {
                onSubmitPrompt();
            }
        },
        [onSubmitPrompt],
    );

    // Focus textfield after generation
    React.useEffect(() => {
        if (!isRefiningContent) {
            setTimeout(() => {
                inputRef.current?.focus({ preventScroll: true });
            }, 0);
        }
    }, [isRefiningContent]);

    // When the index changes, recompute the editor height
    React.useEffect(() => {
        computeDialogHeight();
    }, [historyPosition, computeDialogHeight]);

    // Add a padding bottom to the editor to avoid content being hidden behind the dialog
    // Only if the dialog is not expanded
    React.useEffect(() => {
        const currentEditorRef = editorRef.current;

        const editorContent = currentEditorRef?.querySelector('.wrex-editor') as HTMLDivElement | null;
        if (editorContent) {
            editorContent.style.paddingBottom = isExpanded
                ? '0'
                : `${(dialogRef.current?.getBoundingClientRect().height || 16) - 16}px`;
        }

        return () => {
            if (editorContent) {
                editorContent.style.paddingBottom = '0';
            }
        };
        // Add isRefiningContent to the dependencies to recompute when the dialog is in loading state too
    }, [editorRef, isExpanded, historyPosition]);

    // On open
    React.useEffect(() => {
        // Recompute editor height on scroll or resize
        window.addEventListener('scroll', computeDialogHeight);
        document.querySelector('.lumx-dialog__content')?.addEventListener('scroll', computeDialogHeight);
        window.addEventListener('resize', computeDialogHeight);
        return () => {
            window.removeEventListener('scroll', computeDialogHeight);
            document.querySelector('.lumx-dialog__content')?.removeEventListener('scroll', computeDialogHeight);
            window.removeEventListener('resize', computeDialogHeight);
        };
    }, [computeDialogHeight, editorRef]);

    return (
        <>
            {/* LumX's Popover doesn't forward floating-ui's `padding`, so this boundary gives it the clearance instead. */}
            <div ref={boundaryRef} className={element('viewport-boundary')} aria-hidden="true" />
            {/* PopoverDialog hardcodes closeOnClickAway after the prop spread, so we use Popover directly instead. */}
            <Popover
                anchorRef={anchorRef}
                ref={dialogRef}
                isOpen
                placement={Placement.TOP}
                className={block()}
                role="dialog"
                aria-modal="true"
                closeOnClickAway={false}
                closeOnEscape
                withFocusTrap
                onClose={onCancelRefinement}
                aria-label={pluralize(translateKey(GLOBAL.SUGGESTION), 1)}
                usePortal={false}
                fitWithinViewportHeight
                boundaryRef={boundaryRef}
                // `FitAnchorWidth` isn't exported by @lumx/react, hence the raw string
                fitToAnchorWidth="width"
                maxHeight={`${dialogMaxHeight}px`}
            >
                {/* Reset the heading baseline for headings in the refined suggestion */}
                <HeadingLevelProvider level={2}>
                    <FlexBox orientation={Orientation.vertical}>
                        <FlexBox
                            className={element('header', [margin('bottom', 'regular')])}
                            orientation={Orientation.horizontal}
                            hAlign="top"
                            gap={Size.tiny}
                        >
                            <FlexBox orientation={Orientation.horizontal} hAlign="bottom" gap={Size.tiny}>
                                <Icon icon={mdiSparkles} color={ColorPalette.primary} size={Size.xxs} />
                                <Text as="p" color={ColorPalette.primary} typography={Typography.overline}>
                                    {isRefiningContent
                                        ? translateKey(GLOBAL.CURRENTLY_GENERATING)
                                        : pluralize(translateKey(GLOBAL.SUGGESTION), 1)}
                                </Text>
                            </FlexBox>
                            {dialogHeightSettings.doSuggestionNeedScroll && !shouldOverflowToolbar && (
                                <IconButton
                                    className={element('expand-reduce')}
                                    icon={isExpanded ? mdiMinus : mdiArrowExpand}
                                    emphasis="low"
                                    onClick={() => setIsExpanded((prev) => !prev)}
                                    label={isExpanded ? translateKey(GLOBAL.COLLAPSE) : translateKey(GLOBAL.EXPAND)}
                                />
                            )}
                        </FlexBox>

                        {!isRefiningContent && (
                            <FlexBox
                                orientation={Orientation.vertical}
                                gap={Size.big}
                                className={element('generated-content')}
                            >
                                <div
                                    className={element('suggestion')}
                                    style={{
                                        maxHeight:
                                            isExpanded && dialogHeightSettings.doSuggestionNeedScroll
                                                ? 'none'
                                                : SUGGESTION_MIN_HEIGHT,
                                    }}
                                    ref={suggestionRef}
                                >
                                    <ContentView
                                        theme="light"
                                        content={currentlyDisplayedSlateContent}
                                        elements={WREX_MARKDOWN_ELEMENTS}
                                        marks={WREX_MARKDOWN_MARKS}
                                    />
                                </div>
                                <FlexBox orientation={Orientation.horizontal} gap={Size.regular} noShrink>
                                    <Button
                                        onClick={onCancelRefinement}
                                        emphasis={Emphasis.medium}
                                        size="s"
                                        isDisabled={isRefiningContent}
                                        {...get({ element: 'ignore' })}
                                    >
                                        {translateKey(
                                            refinementHistory && refinementHistory.length > 0
                                                ? WREX_GENERATED_CONTENT.IGNORE_SUGGESTION
                                                : GLOBAL.CANCEL,
                                        )}
                                    </Button>
                                    {/* Show replace button only if some refinement have been performed */}
                                    {refinementHistory && refinementHistory.length > 0 && (
                                        <Button
                                            onClick={() => onInsertNewContent(historyPosition)}
                                            leftIcon={mdiCheckboxMarkedCircleOutline}
                                            size="s"
                                            isDisabled={isRefiningContent}
                                            {...get({ element: 'replace' })}
                                        >
                                            {translateKey(WREX_GENERATED_CONTENT.REPLACE_SELECTION)}
                                        </Button>
                                    )}
                                </FlexBox>
                            </FlexBox>
                        )}

                        {isRefiningContent && <ProgressCircular className={element('loading')} />}

                        <FlexBox
                            className={margin('top', 'huge')}
                            orientation={Orientation.horizontal}
                            gap={Size.regular}
                            vAlign="center"
                        >
                            <TextField
                                inputRef={inputRef}
                                icon={mdiSparkles}
                                placeholder={placeholder}
                                className={element('detailed-prompt')}
                                onChange={setPrompt}
                                afterElement={
                                    !isEmpty(prompt) && (
                                        <Icon
                                            icon={mdiSubdirectoryArrowLeft}
                                            size={Size.xxs}
                                            onClick={onSubmitPrompt}
                                        />
                                    )
                                }
                                isDisabled={isRefiningContent}
                                value={prompt}
                                onKeyDown={onKeyDown}
                            />
                            <Pagination
                                nbOfPages={refinementHistory ? refinementHistory.length : 0}
                                currentPage={historyPosition}
                                onNextClick={() => setHistoryPosition((i) => i + 1)}
                                onPreviousClick={() => setHistoryPosition((i) => i - 1)}
                                isFullWidth={false}
                                mode={PaginationMode.compact}
                            />
                        </FlexBox>
                    </FlexBox>
                </HeadingLevelProvider>
            </Popover>
        </>
    );
};
